import { useRef, useState } from 'react';
import HotFilterButton from '@components/organisms/hot-filters/hot-filter-button/hot-filter-button';
import { HotFilterGroupConfiguration, HotFilterTypes } from '@config/catalog/hotfilter-group-configurations';
import { useTranslation } from 'next-i18next';
import HotFilterDialog from '@components/organisms/hot-filters/hot-filter-dialog/hot-filter-dialog';
import CatalogHotfilterList from '@components/catalog/catalog-hotfilters/catalog-hotfilter-list/catalog-hotfilter-list';
import Button from '@components/atoms/button/button';
import useCatalogHotfilterItem from '@hooks/filters/use-catalog-hotfilter-item';
import clsx from 'clsx';
import { useContextSelector } from 'use-context-selector';
import { CatalogContext } from '@context/catalog.context';
import useWindowSize from '@hooks/use-window-size';
import { handleMobileFilterBodyClass } from '@helpers/utils/catalog/catalog-ui-utils';
import styles from './catalog-hotfilter-item.module.scss';

interface CatalogHotfilterItemProps {
  filterGroup: HotFilterGroupConfiguration;
  setIsFilterOpen: (state: boolean) => void;
}

const POPUP_WIDTH_IN_PX = 400;

const CatalogHotfilterItem = ({ filterGroup, setIsFilterOpen }: CatalogHotfilterItemProps) => {
  const [dialogState, setDialogState] = useState<{ open: boolean; offset: 'right' | 'left' }>({
    open: false,
    offset: 'right',
  });
  const { isWiderThanMd } = useWindowSize();
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const staticFilterGroups = useContextSelector(CatalogContext, (v) => v.staticFilterGroups);

  const { checkedItemCount, handleClearFilters, isStatic } = useCatalogHotfilterItem({ filterGroup });

  const handleOpenDialog = () => {
    if (dialogState.open) {
      setDialogState({ open: false, offset: 'right' });
      handleMobileFilterBodyClass(false);
      return;
    }
    const rect = buttonRef.current.getBoundingClientRect();
    const rightSpace = window.innerWidth - rect.right;
    const leftSpace = rect.left;

    const getOffset = () => {
      if (rightSpace >= POPUP_WIDTH_IN_PX) {
        return 'right';
      }
      if (leftSpace >= POPUP_WIDTH_IN_PX) {
        return 'left';
      }
      return 'right';
    };

    setDialogState({ open: true, offset: getOffset() });
    setIsFilterOpen(true);
    handleMobileFilterBodyClass(true, isWiderThanMd);
  };

  const handleCloseDialog = () => {
    setDialogState({ open: false, offset: 'right' });
    setIsFilterOpen(false);
    handleMobileFilterBodyClass(false);
  };

  const clearFiltersButtonDisabled = checkedItemCount === 0 || isStatic;

  if (filterGroup.type === HotFilterTypes.CATEGORY && staticFilterGroups.categoryLvl2) {
    return null;
  }

  if (filterGroup.type === HotFilterTypes.MATERIAL && staticFilterGroups.materialLvl1) {
    return null;
  }

  return (
    <div ref={containerRef}>
      <HotFilterButton
        ref={buttonRef}
        label={filterGroup.label}
        onClick={() => handleOpenDialog()}
        checkedItemCount={checkedItemCount}
        isSelected={dialogState.open}
        dataCy={`${filterGroup.label}.BUTTON`}
        hasLiveRegion={true}
      />
      {dialogState.open && (
        <HotFilterDialog
          customClass={clsx(
            styles.catalogHotfilterItem__dialog,
            filterGroup.type === HotFilterTypes.SIZE && styles.catalogHotfilterItem__dialog__sizes,
            filterGroup.type === HotFilterTypes.CATEGORY && styles.catalogHotfilterItem__dialog__category,
            filterGroup.type === HotFilterTypes.MATERIAL && styles.catalogHotfilterItem__dialog__category,
          )}
          dataCy={`${filterGroup.label}.DIALOG`}
          headerLabel={filterGroup.label}
          isDialogOpened={true}
          onClose={() => handleCloseDialog()}
          handleApplyChange={() => handleCloseDialog()}
          handleClear={() => {}}
          offset={dialogState.offset}
          clickOutsideRef={containerRef}
          showCtaSection={false}
          hasChildDialog={filterGroup.type === HotFilterTypes.SIZE}
        >
          <div className={styles.catalogHotfilterItem__wrapper}>
            <CatalogHotfilterList filterGroup={filterGroup} />
          </div>
          <div className={styles.catalogHotfilterItem__actionRow}>
            <Button
              disableDefaultStyling
              onClick={() => {
                handleClearFilters();
                handleCloseDialog();
              }}
              variant={isWiderThanMd ? 'ghost' : 'secondary'}
              size={isWiderThanMd ? 'none' : 'large'}
              disabled={clearFiltersButtonDisabled}
              className={clsx(styles.catalogHotfilterItem__actionButton, clearFiltersButtonDisabled && 'vc-d-md-none')}
              data-cy={`${filterGroup.label}.CLEAR_BUTTON`}
            >
              {t('CATALOG.FILTERS.CLEAR')}
            </Button>
            <Button
              disableDefaultStyling
              onClick={handleCloseDialog}
              variant={isWiderThanMd ? 'ghost' : 'primary'}
              size={isWiderThanMd ? 'none' : 'large'}
              className={clsx(styles.catalogHotfilterItem__actionButton, styles.catalogHotfilterItem__closeButton)}
              data-cy={`${filterGroup.label}.CLOSE_BUTTON`}
            >
              {t('MODAL.BUTTON.CLOSE')}
            </Button>
          </div>
        </HotFilterDialog>
      )}
    </div>
  );
};

export default CatalogHotfilterItem;
